/* ═════════════════════════ Login page ═══════════════════════════════
   Single sign-in method: "Continue with Google" (GET /api/auth/google,
   full-page redirect). The first person to sign in becomes the admin;
   after that only emails invited from Settings → Team Members can sign in.

   Visual language matches the rest of the app (Inter, #000000 on #F4F4F5,
   #000000 for primary CTAs — same as every other button in the app).
   ═══════════════════════════════════════════════════════════════════ */

const COLOR_INK = "#000000";
const COLOR_MUTED = "#6B7280";
const COLOR_BORDER = "#E4E4E7";
const COLOR_SOFT = "#F4F4F5";

export default function Login({ welcomeFromCheckout = false } = {}) {
  // Surface server-side errors (e.g. ?error=not_invited after a Google redirect).
  const params = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
  const error = params?.get("error");

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: 24,
      gap: 32,
      background: COLOR_SOFT,
      fontFamily: "Inter, sans-serif",
    }}>
      <img
        src="/brand/elion-roaster.svg"
        alt="Elion Roaster"
        style={{ height: 64, width: "auto", display: "block" }}
      />

      <div style={{
        width: "100%",
        maxWidth: 420,
        background: "#fff",
        borderRadius: 14,
        border: `1px solid ${COLOR_BORDER}`,
        padding: 32,
        boxShadow: "0 8px 24px rgba(17,24,39,0.04)",
      }}>
        <h1 style={{ fontSize: 22, fontWeight: 600, color: COLOR_INK, margin: "0 0 6px" }}>
          {welcomeFromCheckout ? "Your roastery just got an upgrade" : "Sign in"}
        </h1>
        <p style={{ fontSize: 14, color: COLOR_MUTED, margin: "0 0 24px", lineHeight: 1.5 }}>
          {welcomeFromCheckout
            ? "Sign in with Google to spin up your workspace and put your production on rails. Your subscription will attach automatically."
            : "If it's your first time signing in, we'll create an account for you."}
        </p>

        {error && (
          <div style={{
            marginBottom: 16, padding: "10px 14px", borderRadius: 8,
            background: "#FEF2F2", border: "1px solid #FECACA", color: "#991B1B",
            fontSize: 13, lineHeight: 1.5,
          }}>
            {errorMessage(error)}
          </div>
        )}

        <a
          href="/api/auth/google"
          style={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
            padding: "12px 16px", borderRadius: 10,
            border: `1px solid ${COLOR_INK}`, background: COLOR_INK,
            color: "#fff", fontSize: 15, fontWeight: 600,
            textDecoration: "none", fontFamily: "Inter, sans-serif",
          }}
        >
          <GoogleLogo dark />
          Continue with Google
        </a>
      </div>
    </div>
  );
}

function errorMessage(code) {
  switch (code) {
    case "not_invited":
      return "That Google account isn't on the team yet. Ask an admin to invite your email from Settings → Team members.";
    case "google_not_configured":
      return "Google sign-in isn't configured on this server. Add GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET to .env and restart.";
    case "oauth_failed":
      return "Google sign-in didn't complete. Please try again.";
    default:
      return "Sign-in failed. Please try again.";
  }
}

function GoogleLogo({ dark = false }) {
  // On a dark CTA the multicolor Google mark sits inside a white chip for
  // legibility (Google's brand guidelines).
  const mark = (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
      <path fill="#4285F4" d="M17.64 9.2045c0-.6381-.0573-1.2518-.1636-1.8409H9v3.4814h4.8436c-.2086 1.125-.8432 2.0782-1.7959 2.7164v2.2581h2.9087c1.7018-1.5668 2.6836-3.874 2.6836-6.615z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.4673-.806 5.9564-2.1805l-2.9087-2.2581c-.8059.54-1.8373.86-3.0477.86-2.344 0-4.3282-1.5818-5.0359-3.7104H.9574v2.3318C2.4382 15.9832 5.4818 18 9 18z"/>
      <path fill="#FBBC05" d="M3.9641 10.71c-.18-.54-.2823-1.1168-.2823-1.71s.1023-1.17.2823-1.71V4.9582H.9574C.3477 6.1732 0 7.5477 0 9s.3477 2.8268.9574 4.0418L3.9641 10.71z"/>
      <path fill="#EA4335" d="M9 3.5795c1.3214 0 2.5077.4541 3.4405 1.346L15.0218 2.345C13.4632.8568 11.426 0 9 0 5.4818 0 2.4382 2.0168.9574 4.9582L3.9641 7.29C4.6718 5.1614 6.656 3.5795 9 3.5795z"/>
    </svg>
  );
  if (!dark) return mark;
  return (
    <span style={{
      background: "#fff", borderRadius: 4, padding: 3,
      display: "inline-flex", alignItems: "center", justifyContent: "center",
    }}>{mark}</span>
  );
}
